import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { userEducationService, EducationEntry, EducationEntryRequest } from '../services/userEducationService';
import { userLanguageService } from '../services/userLanguageService';
import { SUPPORTED_LANGUAGES } from '../constants/languages';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  Chip,
  IconButton,
  Divider,
  OutlinedInput,
} from '@mui/material';
import { FamilyRestroom, School, AddCircleOutline, Delete } from '@mui/icons-material';

const SCHOOL_TYPES = ['Primary', 'Secondary'];

interface ParentForm {
  phoneNumber: string;
}

interface StudentForm {
  schoolCountry: string;
  schoolCouncil: string;
  schoolName: string;
  schoolType: string;
  courseInterests: string;
  phoneNumber: string;
}

interface TutorForm {
  profession: string;
  languages: string[];
}

interface TeacherForm {
  profession: string;
  institutionName: string;
  institutionCity: string;
  institutionCountry: string;
  institutionType: string;
  subjectsTaught: string;
  languages: string[];
}

interface NewEntryDraft {
  entryType: 'UNIVERSITY_DEGREE' | 'PRE_UNIVERSITY';
  universityName: string;
  subjectStudied: string;
  collegeName: string;
  courseName: string;
  subjects: string;
  city: string;
  country: string;
  yearOfCompletion: string;
}

const emptyDraft = (): NewEntryDraft => ({
  entryType: 'UNIVERSITY_DEGREE',
  universityName: '', subjectStudied: '', collegeName: '',
  courseName: '', subjects: '', city: '', country: '', yearOfCompletion: '',
});

const RoleActivationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'PARENT').toUpperCase();

  const [parentForm, setParentForm] = useState<ParentForm>({ phoneNumber: '' });
  const [studentForm, setStudentForm] = useState<StudentForm>({
    schoolCountry: '', schoolCouncil: '', schoolName: '',
    schoolType: '', courseInterests: '', phoneNumber: '',
  });
  const [tutorForm, setTutorForm] = useState<TutorForm>({ profession: '', languages: [] });
  const [teacherForm, setTeacherForm] = useState<TeacherForm>({
    profession: '', institutionName: '', institutionCity: '',
    institutionCountry: '', institutionType: '', subjectsTaught: '', languages: [],
  });
  const [educationEntries, setEducationEntries] = useState<EducationEntry[]>([]);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [entryDraft, setEntryDraft] = useState<NewEntryDraft>(emptyDraft());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);

  useEffect(() => {
    if (role === 'TUTOR' || role === 'TEACHER') {
      userEducationService.getEducationEntries().then(setEducationEntries).catch(() => {});
      userLanguageService.getLanguages().then(langs => {
        if (langs.length > 0) {
          if (role === 'TUTOR') setTutorForm(f => ({ ...f, languages: langs }));
          else setTeacherForm(f => ({ ...f, languages: langs }));
        }
      }).catch(() => {});
    }
  }, [role]);

  const validateParent = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!parentForm.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStudent = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!studentForm.schoolCountry.trim()) newErrors.schoolCountry = 'Country is required';
    if (!studentForm.schoolCouncil.trim()) newErrors.schoolCouncil = 'School council is required';
    if (!studentForm.schoolName.trim()) newErrors.schoolName = 'School name is required';
    if (!studentForm.schoolType) newErrors.schoolType = 'School type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTeacher = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!teacherForm.profession.trim()) newErrors.profession = 'Profession is required';
    if (!teacherForm.institutionName.trim()) newErrors.institutionName = 'School / institution name is required';
    if (teacherForm.languages.length === 0) newErrors.languages = 'Select at least one language';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTutor = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!tutorForm.profession.trim()) newErrors.profession = 'Profession is required';
    if (tutorForm.languages.length === 0) newErrors.languages = 'Select at least one language';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddEntry = async () => {
    const newErrors: Record<string, string> = {};
    if (!entryDraft.city.trim()) newErrors.entryCity = 'City is required';
    if (!entryDraft.country.trim()) newErrors.entryCountry = 'Country is required';
    if (!entryDraft.yearOfCompletion) newErrors.entryYear = 'Year is required';
    if (entryDraft.entryType === 'UNIVERSITY_DEGREE' && !entryDraft.universityName.trim())
      newErrors.entryUniversity = 'University name is required';
    if (entryDraft.entryType === 'PRE_UNIVERSITY' && !entryDraft.collegeName.trim())
      newErrors.entryCollege = 'College name is required';
    if (Object.keys(newErrors).length > 0) { setErrors(prev => ({ ...prev, ...newErrors })); return; }

    try {
      const req: EducationEntryRequest = {
        entryType: entryDraft.entryType,
        city: entryDraft.city,
        country: entryDraft.country,
        yearOfCompletion: parseInt(entryDraft.yearOfCompletion),
        ...(entryDraft.entryType === 'UNIVERSITY_DEGREE'
          ? { universityName: entryDraft.universityName, subjectStudied: entryDraft.subjectStudied }
          : { collegeName: entryDraft.collegeName, courseName: entryDraft.courseName, subjects: entryDraft.subjects }),
      };
      const saved = await userEducationService.addEducationEntry(req);
      setEducationEntries(prev => [...prev, saved]);
      setEntryDraft(emptyDraft());
      setShowAddEntry(false);
    } catch (err: any) {
      setActivationError(err.message || 'Failed to save education entry');
    }
  };

  const handleDeleteEntry = async (id: number) => {
    try {
      await userEducationService.deleteEducationEntry(id);
      setEducationEntries(prev => prev.filter(e => e.id !== id));
    } catch {
      setActivationError('Failed to remove education entry');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let valid = false;
    if (role === 'PARENT') valid = validateParent();
    else if (role === 'STUDENT') valid = validateStudent();
    else if (role === 'TUTOR') valid = validateTutor();
    else if (role === 'TEACHER') valid = validateTeacher();
    else valid = true;
    if (!valid) return;

    setLoading(true);
    setActivationError(null);
    try {
      let data: Record<string, any> = {};
      if (role === 'PARENT') data = { phoneNumber: parentForm.phoneNumber };
      else if (role === 'STUDENT') data = {
        schoolCountry: studentForm.schoolCountry,
        schoolCouncil: studentForm.schoolCouncil,
        schoolName: studentForm.schoolName,
        schoolType: studentForm.schoolType,
        courseInterests: studentForm.courseInterests || undefined,
        phoneNumber: studentForm.phoneNumber || undefined,
      };
      else if (role === 'TUTOR') data = { profession: tutorForm.profession };
      else if (role === 'TEACHER') data = {
        profession: teacherForm.profession,
        institutionName: teacherForm.institutionName || undefined,
        institutionCity: teacherForm.institutionCity || undefined,
        institutionCountry: teacherForm.institutionCountry || undefined,
        institutionType: teacherForm.institutionType || undefined,
        subjectsTaught: teacherForm.subjectsTaught || undefined,
      };

      await authService.activateRole(role, data);

      if (role === 'TUTOR' && tutorForm.languages.length > 0) {
        await userLanguageService.setLanguages(tutorForm.languages).catch(() => {});
      }
      if (role === 'TEACHER' && teacherForm.languages.length > 0) {
        await userLanguageService.setLanguages(teacherForm.languages).catch(() => {});
      }

      window.dispatchEvent(new Event('userLogin'));

      const dashboardMap: Record<string, string> = {
        STUDENT: '/student-dashboard',
        PARENT: '/parent-dashboard',
        TUTOR: '/tutor-dashboard',
        TEACHER: '/teacher-dashboard',
      };
      navigate(dashboardMap[role] ?? '/role-selection', { replace: true });
    } catch (err: any) {
      setActivationError(err.message || 'Activation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = role ? role.charAt(0) + role.slice(1).toLowerCase() : '';
  const RoleIcon = role === 'PARENT' ? FamilyRestroom : School;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <RoleIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Activate {roleLabel} Role
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete your {roleLabel.toLowerCase()} profile to access your dashboard
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 2, md: 3 } }}>
          <CardContent>
            {activationError && (
              <Alert severity="error" sx={{ mb: 3 }}>{activationError}</Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {role === 'PARENT' && (
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={parentForm.phoneNumber}
                  onChange={(e) => setParentForm({ phoneNumber: e.target.value })}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber || 'Required for account security and communications'}
                  placeholder="+44 7700 900000"
                  sx={{ mb: 3 }}
                />
              )}

              {role === 'STUDENT' && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    School Details
                  </Typography>
                  <TextField fullWidth label="Country" value={studentForm.schoolCountry}
                    onChange={(e) => setStudentForm(p => ({ ...p, schoolCountry: e.target.value }))}
                    error={!!errors.schoolCountry} helperText={errors.schoolCountry}
                    placeholder="Scotland" sx={{ mb: 2 }} />
                  <TextField fullWidth label="School Council / Local Authority" value={studentForm.schoolCouncil}
                    onChange={(e) => setStudentForm(p => ({ ...p, schoolCouncil: e.target.value }))}
                    error={!!errors.schoolCouncil} helperText={errors.schoolCouncil}
                    placeholder="City of Edinburgh" sx={{ mb: 2 }} />
                  <TextField fullWidth label="School Name" value={studentForm.schoolName}
                    onChange={(e) => setStudentForm(p => ({ ...p, schoolName: e.target.value }))}
                    error={!!errors.schoolName} helperText={errors.schoolName} sx={{ mb: 2 }} />
                  <TextField select fullWidth label="School Type" value={studentForm.schoolType}
                    onChange={(e) => setStudentForm(p => ({ ...p, schoolType: e.target.value }))}
                    error={!!errors.schoolType} helperText={errors.schoolType} sx={{ mb: 2 }}>
                    {SCHOOL_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                  <TextField fullWidth label="Phone Number (optional)" value={studentForm.phoneNumber}
                    onChange={(e) => setStudentForm(p => ({ ...p, phoneNumber: e.target.value }))}
                    placeholder="+44 7700 900000" sx={{ mb: 2 }} />
                  <TextField fullWidth multiline rows={2} label="Course Interests (optional)"
                    value={studentForm.courseInterests}
                    onChange={(e) => setStudentForm(p => ({ ...p, courseInterests: e.target.value }))}
                    helperText="e.g. Python, AWS, Data Engineering" sx={{ mb: 3 }} />
                </>
              )}

              {role === 'TUTOR' && (
                <>
                  <TextField fullWidth required label="Current Profession" value={tutorForm.profession}
                    onChange={(e) => setTutorForm(f => ({ ...f, profession: e.target.value }))}
                    error={!!errors.profession} helperText={errors.profession || 'e.g. Maths Tutor, Science Tutor'}
                    sx={{ mb: 3 }} />

                  <FormControl fullWidth error={!!errors.languages} sx={{ mb: 3 }}>
                    <InputLabel>Languages Spoken</InputLabel>
                    <Select multiple value={tutorForm.languages} label="Languages Spoken"
                      input={<OutlinedInput label="Languages Spoken" />}
                      onChange={(e) => setTutorForm(f => ({ ...f, languages: e.target.value as string[] }))}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map(v => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}>
                      {SUPPORTED_LANGUAGES.map(lang => (
                        <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                      ))}
                    </Select>
                    {errors.languages && <FormHelperText>{errors.languages}</FormHelperText>}
                  </FormControl>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">Education ({educationEntries.length}/5)</Typography>
                      {educationEntries.length < 5 && !showAddEntry && (
                        <Button size="small" startIcon={<AddCircleOutline />}
                          onClick={() => setShowAddEntry(true)}>Add Entry</Button>
                      )}
                    </Box>

                    {educationEntries.map(entry => (
                      <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {entry.entryType === 'UNIVERSITY_DEGREE' ? entry.universityName : entry.collegeName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.entryType === 'UNIVERSITY_DEGREE' ? entry.subjectStudied : entry.courseName}
                            {' · '}{entry.city}, {entry.country} · {entry.yearOfCompletion}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => handleDeleteEntry(entry.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    {showAddEntry && (
                      <Box sx={{ p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
                        <TextField select fullWidth label="Entry Type" value={entryDraft.entryType}
                          onChange={(e) => setEntryDraft(d => ({ ...d, entryType: e.target.value as any }))}
                          sx={{ mb: 2 }}>
                          <MenuItem value="UNIVERSITY_DEGREE">University Degree</MenuItem>
                          <MenuItem value="PRE_UNIVERSITY">Pre-University</MenuItem>
                        </TextField>

                        {entryDraft.entryType === 'UNIVERSITY_DEGREE' ? (
                          <>
                            <TextField fullWidth label="University Name" value={entryDraft.universityName}
                              onChange={(e) => setEntryDraft(d => ({ ...d, universityName: e.target.value }))}
                              error={!!errors.entryUniversity} helperText={errors.entryUniversity} sx={{ mb: 2 }} />
                            <TextField fullWidth label="Subject Studied" value={entryDraft.subjectStudied}
                              onChange={(e) => setEntryDraft(d => ({ ...d, subjectStudied: e.target.value }))}
                              sx={{ mb: 2 }} />
                          </>
                        ) : (
                          <>
                            <TextField fullWidth label="College Name" value={entryDraft.collegeName}
                              onChange={(e) => setEntryDraft(d => ({ ...d, collegeName: e.target.value }))}
                              error={!!errors.entryCollege} helperText={errors.entryCollege} sx={{ mb: 2 }} />
                            <TextField fullWidth label="Course Name" value={entryDraft.courseName}
                              onChange={(e) => setEntryDraft(d => ({ ...d, courseName: e.target.value }))}
                              sx={{ mb: 2 }} />
                            <TextField fullWidth label="Subjects (optional)" value={entryDraft.subjects}
                              onChange={(e) => setEntryDraft(d => ({ ...d, subjects: e.target.value }))}
                              sx={{ mb: 2 }} />
                          </>
                        )}

                        <TextField fullWidth label="City" value={entryDraft.city}
                          onChange={(e) => setEntryDraft(d => ({ ...d, city: e.target.value }))}
                          error={!!errors.entryCity} helperText={errors.entryCity} sx={{ mb: 2 }} />
                        <TextField fullWidth label="Country" value={entryDraft.country}
                          onChange={(e) => setEntryDraft(d => ({ ...d, country: e.target.value }))}
                          error={!!errors.entryCountry} helperText={errors.entryCountry} sx={{ mb: 2 }} />
                        <TextField fullWidth label="Year of Completion" type="number" value={entryDraft.yearOfCompletion}
                          onChange={(e) => setEntryDraft(d => ({ ...d, yearOfCompletion: e.target.value }))}
                          error={!!errors.entryYear} helperText={errors.entryYear} sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button variant="outlined" size="small" onClick={() => { setShowAddEntry(false); setEntryDraft(emptyDraft()); }}>
                            Cancel
                          </Button>
                          <Button variant="contained" size="small" onClick={handleAddEntry}>
                            Save Entry
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ mb: 3 }} />
                </>
              )}

              {role === 'TEACHER' && (
                <>
                  <TextField fullWidth required label="Current Profession" value={teacherForm.profession}
                    onChange={(e) => setTeacherForm(f => ({ ...f, profession: e.target.value }))}
                    error={!!errors.profession} helperText={errors.profession || 'e.g. Physics Teacher'}
                    sx={{ mb: 2 }} />
                  <TextField fullWidth required label="School / Institution Name" value={teacherForm.institutionName}
                    onChange={(e) => setTeacherForm(f => ({ ...f, institutionName: e.target.value }))}
                    error={!!errors.institutionName} helperText={errors.institutionName} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField fullWidth label="City" value={teacherForm.institutionCity}
                      onChange={(e) => setTeacherForm(f => ({ ...f, institutionCity: e.target.value }))} />
                    <TextField fullWidth label="Country" value={teacherForm.institutionCountry}
                      onChange={(e) => setTeacherForm(f => ({ ...f, institutionCountry: e.target.value }))} />
                  </Box>
                  <TextField fullWidth label="Institution Type (optional)" value={teacherForm.institutionType}
                    onChange={(e) => setTeacherForm(f => ({ ...f, institutionType: e.target.value }))}
                    placeholder="e.g. Secondary, Primary" sx={{ mb: 2 }} />
                  <TextField fullWidth label="Subjects Taught (optional)" value={teacherForm.subjectsTaught}
                    onChange={(e) => setTeacherForm(f => ({ ...f, subjectsTaught: e.target.value }))}
                    placeholder="e.g. Mathematics, Physics" sx={{ mb: 3 }} />

                  <FormControl fullWidth error={!!errors.languages} sx={{ mb: 3 }}>
                    <InputLabel>Languages Spoken</InputLabel>
                    <Select multiple value={teacherForm.languages} label="Languages Spoken"
                      input={<OutlinedInput label="Languages Spoken" />}
                      onChange={(e) => setTeacherForm(f => ({ ...f, languages: e.target.value as string[] }))}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map(v => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}>
                      {SUPPORTED_LANGUAGES.map(lang => (
                        <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                      ))}
                    </Select>
                    {errors.languages && <FormHelperText>{errors.languages}</FormHelperText>}
                  </FormControl>

                  <Divider sx={{ mb: 3 }} />
                </>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => navigate('/role-selection')} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" fullWidth disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} /> : null}>
                  {loading ? 'Activating...' : `Activate ${roleLabel} Role`}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default RoleActivationPage;
